import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import HomePage from "@/pages/HomePage";
import ProfilePage from "@/pages/ProfilePage";
import AccountPage from "@/pages/AccountPage";
import PracticeSetupPage from "@/pages/PracticeSetupPage";
import PracticeSessionPage from "@/pages/PracticeSessionPage";
import PracticeReviewPage from "@/pages/PracticeReviewPage";
import HistoryPage from "@/pages/HistoryPage";
import AllSessionsPage from "@/pages/AllSessionsPage";
import LearnModulesPage from "@/pages/LearnModulesPage";
import LearnModulePage from "@/pages/LearnModulePage";
import LearnChapterPage from "@/pages/LearnChapterPage";
import LearnQuizPage from "@/pages/LearnQuizPage";
import AdminOverviewPage from "@/pages/admin/AdminOverviewPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AdminUserDetailPage from "@/pages/admin/AdminUserDetailPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import AdminSupportPage from "@/pages/admin/AdminSupportPage";
import AdminSupportThreadPage from "@/pages/admin/AdminSupportThreadPage";
import AdminAllowlistPage from "@/pages/admin/AdminAllowlistPage";
import SubscribePage from "@/pages/SubscribePage";
import SupportListPage from "@/pages/support/SupportListPage";
import SupportNewPage from "@/pages/support/SupportNewPage";
import SupportThreadPage from "@/pages/support/SupportThreadPage";
import { AdminRoute } from "@/components/AdminRoute";
import NotFoundPage from "@/pages/NotFoundPage";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: 1, refetchOnWindowFocus: false },
    },
});

const PROTECTED_PAGES = [
    { path: "/", element: <HomePage /> },
    { path: "/profile", element: <ProfilePage /> },
    { path: "/account", element: <AccountPage /> },
    { path: "/practice/setup", element: <PracticeSetupPage /> },
    { path: "/practice/session/:id", element: <PracticeSessionPage /> },
    { path: "/practice/review/:id", element: <PracticeReviewPage /> },
    { path: "/history", element: <HistoryPage /> },
    { path: "/all-sessions", element: <AllSessionsPage /> },
    { path: "/learn", element: <LearnModulesPage /> },
    { path: "/learn/chapter/:slug", element: <LearnChapterPage /> },
    { path: "/learn/chapter/:slug/quiz", element: <LearnQuizPage /> },
    { path: "/learn/:module_slug", element: <LearnModulePage /> },
    { path: "/subscribe", element: <SubscribePage /> },
    { path: "/support", element: <SupportListPage /> },
    { path: "/support/new", element: <SupportNewPage /> },
    { path: "/support/:id", element: <SupportThreadPage /> },
];

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        {/* Admin section — gated by AdminRoute (auth + role==="admin") */}
                        <Route
                            path="/admin"
                            element={<AdminRoute><AdminOverviewPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/users"
                            element={<AdminRoute><AdminUsersPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/users/:id"
                            element={<AdminRoute><AdminUserDetailPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/payments"
                            element={<AdminRoute><AdminPaymentsPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/support"
                            element={<AdminRoute><AdminSupportPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/support/:id"
                            element={<AdminRoute><AdminSupportThreadPage /></AdminRoute>}
                        />
                        <Route
                            path="/admin/allowlist"
                            element={<AdminRoute><AdminAllowlistPage /></AdminRoute>}
                        />
                        {PROTECTED_PAGES.map(({ path, element }) => (
                            <Route
                                key={path}
                                path={path}
                                element={<ProtectedRoute>{element}</ProtectedRoute>}
                            />
                        ))}
                        {/* 404 — unknown route catch-all. Wrapped in ProtectedRoute
                            so the layout & nav are visible only to authenticated users;
                            unauthenticated requests bounce to /login like every other page. */}
                        <Route
                            path="*"
                            element={
                                <ProtectedRoute>
                                    <NotFoundPage />
                                </ProtectedRoute>
                            }
                        />
                    </Routes>
                    <Toaster position="top-right" richColors closeButton />
                </AuthProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}

export default App;
